import { booleanAttribute } from "#ce/utils/props";

const locationOptions = ["bottom", "bottom-left", "bottom-right", "top", "top-left", "top-right"];

export const props = Object.freeze({
  /**
   * Preserve default actions and propagation for clicks inside the content.
   *
   * @type {Boolean|String}
   * @default false
   */
  nativeContentClicks: booleanAttribute(),
  /**
   * Suppress focus restoration on close when the caller owns the next focus target.
   *
   * @type {Boolean|String}
   * @default false
   */
  noRestoreFocus: booleanAttribute(),
  /**
   * Accessible name for the menu's bottom sheet, passed down because the visible title sits outside this
   * shadow root where an `aria-labelledby` IDREF cannot reach it.
   *
   * @type {String}
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * Declarative open state. Two-way via update-open event. Call the host `show()` and `hide()` methods for imperative state changes.
   *
   * @type {Boolean|String}
   * @default false
   */
  open: booleanAttribute(),
  /**
   * Suppress menu activation entirely. The activator still renders but clicking it does nothing.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Close the menu when a click occurs inside the content. Disable for menus where users need to interact with the content (forms, multi-select) without dismissing.
   *
   * @type {Boolean|String}
   * @default true
   */
  closeOnContentClick: booleanAttribute(true),
  /**
   * Close the menu when a click occurs outside the activator and the content.
   *
   * @type {Boolean|String}
   * @default true
   */
  closeOnOutsideClick: booleanAttribute(true),
  /**
   * Show an arrow on the popup pointing at the activator. Has no effect on the mobile bottom-sheet variant.
   *
   * @type {Boolean|String}
   * @default false
   */
  pointer: booleanAttribute(),
  /**
   * Keep the popup below the small-screen breakpoint instead of presenting a bottom sheet.
   *
   * @type {Boolean|String}
   * @default false
   */
  noSheet: booleanAttribute(),
  /**
   * On mobile, expand the bottom-sheet to its maximum height when the menu opens.
   *
   * @type {Boolean|String}
   * @default false
   */
  mobileExpandHeightMax: booleanAttribute(),
  /**
   * Size the desktop popup to its content: width `max-content`, no minimum width. Overrides the
   * `--sp-comp-menu-width` and `--sp-comp-menu-min-width` tokens.
   *
   * @type {Boolean|String}
   * @default false
   */
  fitContent: booleanAttribute(),
  /**
   * Keep the requested location when the popup reaches a viewport edge.
   *
   * @type {Boolean|String}
   * @default false
   */
  noFlip: booleanAttribute(),
  /**
   * Anchor position of the popup relative to the activator. "bottom" and "top" centre the popup on that side; "*-left" anchors to the activator's start edge; "*-right" anchors to the end edge.
   *
   * @type {String}
   * @default "bottom"
   * @storybook-control select
   */
  location: {
    type: String,
    default: "bottom",
    validator: (value) => locationOptions.includes(value),
  },
  /**
   * Keep the menu open at all times and remove the activator. Useful when sp-menu is embedded inside another surface that already handles open / close (e.g. the desktop column of a layout that shows a sidebar inline).
   *
   * @type {Boolean|String}
   * @default false
   */
  permanent: booleanAttribute(),
});
